import { useCallback, useState } from 'react';
import { FlatList, Text, View } from 'react-native';
import { useFocusEffect, useLocalSearchParams, useRouter } from 'expo-router';
import { useAuth } from '../context/AuthContext';
import { getShop } from '../api/shops';
import { cancelTask, myTasks } from '../api/tasks';
import { ReviewTask, Shop } from '../types';
import { Button, Card, ConfirmDialog, EmptyState, ErrorText, LoadingState, ScreenContainer } from '../components/ui';
import { colors, fonts } from '../theme';
import { openExternalUrl } from '../utils/openUrl';
import { ApiError } from '../api/client';

const TASK_STATUS_LABELS: Record<string, string> = {
  ACTIVE: 'Đang hoạt động',
  PAUSED: 'Tạm dừng',
  COMPLETED: 'Đã hoàn thành',
  CANCELLED: 'Đã huỷ',
};

export default function ShopDetailScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const { token } = useAuth();
  const router = useRouter();
  const [shop, setShop] = useState<Shop | null>(null);
  const [tasks, setTasks] = useState<ReviewTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [cancelTargetId, setCancelTargetId] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!token || !id) return;
    setLoading(true);
    try {
      const [shopData, allTasks] = await Promise.all([getShop(token, id), myTasks(token)]);
      setShop(shopData);
      setTasks(allTasks.filter((t) => t.shopId === id));
      setLoadError(null);
    } catch (e) {
      setLoadError(e instanceof ApiError ? e.message : 'Không tải được thông tin shop, vui lòng thử lại.');
    } finally {
      setLoading(false);
    }
  }, [token, id]);

  useFocusEffect(
    useCallback(() => {
      load();
    }, [load]),
  );

  const onConfirmCancel = async () => {
    if (!token || !cancelTargetId) return;
    setCancelling(true);
    setCancelError(null);
    try {
      await cancelTask(token, cancelTargetId);
      setCancelTargetId(null);
      await load();
    } catch (e) {
      setCancelError(e instanceof ApiError ? e.message : 'Dừng nhiệm vụ thất bại');
    } finally {
      setCancelling(false);
    }
  };

  if (!shop) {
    if (loadError) {
      return (
        <ScreenContainer>
          <ErrorText>{loadError}</ErrorText>
          <Button title="Thử lại" onPress={load} />
        </ScreenContainer>
      );
    }
    return <LoadingState />;
  }

  return (
    <ScreenContainer>
      <Card>
        <Text style={{ fontSize: 18, fontFamily: fonts.bold, color: colors.text }}>{shop.name}</Text>
        <Text style={{ color: colors.textMuted, marginTop: 4 }}>{shop.address}</Text>
        {shop.description ? (
          <Text style={{ color: colors.textMuted, marginTop: 4 }}>{shop.description}</Text>
        ) : null}
        <Button
          title="Sửa thông tin shop"
          variant="outline"
          onPress={() => router.push({ pathname: '/create-shop', params: { shopId: shop.id } })}
        />
        <Button
          title="Xem trên Google Maps"
          variant="outline"
          onPress={() =>
            openExternalUrl(
              `https://www.google.com/maps/search/?api=1&query=${shop.latitude},${shop.longitude}`,
            )
          }
        />
      </Card>

      <View style={{ marginBottom: 12 }}>
        <Button
          title="+ Tạo nhiệm vụ mới cho shop này"
          onPress={() => router.push({ pathname: '/create-task', params: { shopId: shop.id } })}
        />
      </View>

      <Text style={{ fontFamily: fonts.bold, color: colors.text, marginBottom: 8 }}>
        Nhiệm vụ đã tạo
      </Text>
      <FlatList
        data={tasks}
        keyExtractor={(item) => item.id}
        refreshControl={undefined}
        ListEmptyComponent={!loading ? <EmptyState text="Chưa có nhiệm vụ nào" /> : null}
        renderItem={({ item }) => (
          <Card>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
              <Text style={{ fontFamily: fonts.bold, color: colors.text }}>{item.title}</Text>
              <Text style={{ color: colors.primary, fontFamily: fonts.semiBold }}>
                {item.rewardCoins} xu
              </Text>
            </View>
            <Text style={{ color: colors.textMuted, marginTop: 4, fontSize: 12 }}>
              {item.filledSlots}/{item.totalSlots} lượt đã hoàn thành · {TASK_STATUS_LABELS[item.status] ?? item.status}
            </Text>
            {item.status === 'ACTIVE' ? (
              <Button
                title="Dừng nhiệm vụ"
                variant="danger"
                onPress={() => setCancelTargetId(item.id)}
              />
            ) : null}
          </Card>
        )}
      />

      <ConfirmDialog
        visible={cancelTargetId !== null}
        title="Dừng nhiệm vụ"
        message="Nhiệm vụ sẽ ngừng hiển thị cho người nhận việc và không thể mở lại. Số xu đã giữ cho các lượt CHƯA có người làm sẽ được hoàn lại vào ví — các lượt đang thực hiện dở vẫn được xử lý bình thường."
        confirmLabel="Dừng nhiệm vụ"
        danger
        loading={cancelling}
        onConfirm={onConfirmCancel}
        onCancel={() => {
          setCancelTargetId(null);
          setCancelError(null);
        }}
      >
        <ErrorText>{cancelError}</ErrorText>
      </ConfirmDialog>
    </ScreenContainer>
  );
}
